/**
 * Ticket detail pane: header (tenant, status, assignee, property/room,
 * channel, email) plus Conversation / Internal notes tabs over the shared
 * IssueChatThread.
 *
 * Only `ticketId` identifies the ticket — there is no `ticket` data prop —
 * so this component fetches its own single-row read model (status/assignee/
 * property/room/channel + tenant & assignee names hydrated from profiles,
 * same FK-boundary reasoning as useSupportInbox.ts: issues.reported_by and
 * .assigned_to reference auth.users, which PostgREST cannot embed
 * `profiles` across). Status and assignee changes are NOT mutated here —
 * `onStatusChange`/`onAssign` hand the write to the caller, this component
 * only reflects fetched state and refetches its own row afterward.
 *
 * The one write this component DOES own is "Link to tenant…" on an UNOWNED
 * ticket — one inbound-email-webhook filed for a sender who matched no user
 * (reported_by/user_id NULL, sender kept in metadata.from_email;
 * 20260914100000_issues_unowned_tickets.sql). Linking is an UPDATE of that
 * same row guarded on `user_id IS NULL`, never a second ticket, and this pane
 * is the only place it can happen: such mail no longer parks in a separate
 * triage queue.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, ChevronDown, Search } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useLanguage } from "@/contexts/LanguageContext";
import { TenantProfileLauncher } from "./TenantProfileLauncher";
import {
  readSupportPresentation,
  SupportPresentationCards,
} from "./SupportPresentationCards";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { IssueChatThread } from "@/components/issues/IssueChatThread";
import { useAdminRoster, adminDisplayName } from "@/hooks/useAdminRoster";
import { safeOrTerm } from "@/lib/utils";
import {
  SUPPORT_CHANNELS,
  inboundSenderEmail,
  inboundSenderPhone,
  type SupportChannel,
} from "@/lib/support-views";

const UNASSIGNED = "unassigned";

type ProfileBrief = { id: string; name: string | null; email: string | null };

const STATUS_OPTIONS: { value: string; i18nKey: string; fallback: string }[] = [
  { value: "open", i18nKey: "support.status.open", fallback: "Open" },
  {
    value: "in_progress",
    i18nKey: "support.status.inProgress",
    fallback: "In progress",
  },
  { value: "waiting", i18nKey: "support.status.waiting", fallback: "Waiting" },
  {
    value: "resolved",
    i18nKey: "support.status.resolved",
    fallback: "Resolved",
  },
];

type TicketDetailRow = {
  id: string;
  title: string;
  archived_at: string | null;
  status: string;
  channel: SupportChannel;
  assigned_to: string | null;
  contact_type: string | null;
  /** NULL = unowned ticket (unknown sender); see the file header. */
  reported_by: string | null;
  /** `issues.metadata` — carries `from_email` on email-sourced tickets. */
  metadata?: unknown;
  properties?: { title?: string | null; address?: string | null } | null;
  rooms?: { title?: string | null; size_sqm?: number | null } | null;
  tenantName: string;
  tenantEmail: string | null;
  assignedName: string | null;
  /**
   * The address the mail was ORIGINALLY addressed to, off the newest inbound
   * row. Three forwards (info@ / contact@ / kontakt@) land in one inbox, so
   * without this the header cannot say which door a sender came through.
   * NULL on ticket/WhatsApp channels and on an email whose inbound row
   * predates to_address.
   */
  inboundToAddress: string | null;
  inboundOriginalText: string | null;
};

interface TicketDetailProps {
  ticketId: string | null;
  supportAppearance?: boolean;
  onStatusChange?: (status: string) => void | Promise<void>;
  onAssign?: (userId: string | null) => void | Promise<void>;
  onConversationSent?: () => void;
  focusCommentId?: string | null;
}

export function TicketDetail({
  ticketId,
  supportAppearance = false,
  onStatusChange,
  onAssign,
  onConversationSent,
  focusCommentId,
}: TicketDetailProps) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const currentUserId = user?.id ?? "";
  const { roster, error: rosterError } = useAdminRoster();
  const queryClient = useQueryClient();
  const [linkOpen, setLinkOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [linkSearch, setLinkSearch] = useState("");

  // Internal-notes count for the tab badge. A HEAD request with an exact
  // count — the notes themselves are fetched by IssueChatThread when the tab
  // is actually opened, and pulling them twice to show a number would be
  // wasteful. Soft-deleted notes don't count: they aren't rendered either.
  const { data: internalCount = 0 } = useQuery({
    queryKey: ["support-ticket-detail", ticketId, "internal-count"],
    enabled: !!ticketId,
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from("issue_comments")
        .select("id", { count: "exact", head: true })
        .eq("issue_id", ticketId)
        .eq("is_internal", true)
        .is("deleted_at", null);
      if (error) throw error;
      return count ?? 0;
    },
  });

  const {
    data: ticket,
    error: ticketError,
    refetch,
  } = useQuery({
    queryKey: ["support-ticket-detail", ticketId],
    queryFn: async (): Promise<TicketDetailRow | null> => {
      const { data, error } = await supabase
        .from("issues")
        .select(
          "*, properties:property_id (id, title, address), rooms:room_id (id, title, size_sqm)"
        )
        .eq("id", ticketId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;

      const ids = Array.from(
        new Set(
          [data.reported_by, data.assigned_to].filter(Boolean).map(String)
        )
      );
      const byId = new Map<
        string,
        { id: string; name: string | null; email: string | null }
      >();
      if (ids.length) {
        const { data: profs, error: profErr } = await supabase
          .from("profiles")
          .select("id, name, email")
          .in("id", ids);
        if (profErr) throw profErr;
        for (const p of profs ?? []) byId.set(String(p.id), p);
      }

      // Same row send-ticket-reply picks the reply's From address from
      // (supabase/functions/send-ticket-reply/index.ts) — newest inbound with
      // a to_address — so the header names the address a reply would go out
      // as. Only email tickets have one; skip the round trip otherwise.
      let inboundToAddress: string | null = null;
      let inboundOriginalText: string | null = null;
      if (data.channel === "email") {
        const { data: inbound, error: inboundErr } = await supabase
          .from("inbound_messages")
          .select("to_address,body_text")
          .eq("issue_id", ticketId)
          .not("to_address", "is", null)
          .order("received_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        // Never fatal: the ticket is readable without it, and failing the
        // whole detail view over a cosmetic label is the worse trade.
        if (!inboundErr) {
          inboundToAddress = inbound?.to_address ?? null;
          inboundOriginalText = inbound?.body_text ?? null;
        }
      }

      const tenant = data.reported_by
        ? byId.get(String(data.reported_by))
        : null;
      const assignee = data.assigned_to
        ? byId.get(String(data.assigned_to))
        : null;
      return {
        ...data,
        // issues.channel is plain text in the DB; the CHECK constraint keeps
        // it to the SupportChannel values.
        channel: data.channel as SupportChannel,
        tenantName:
          tenant?.name?.trim() ||
          tenant?.email?.trim() ||
          // An UNOWNED ticket has no profile to name it after; the address or
          // number it came from is the honest label (same chain
          // useSupportInbox.ts uses, so the list row and this header agree).
          inboundSenderEmail(data.metadata) ||
          inboundSenderPhone(data.metadata) ||
          t("adminIssues.unknown", "Unknown"),
        tenantEmail: tenant?.email ?? null,
        // Same name/email/fallback chain as the roster's own display name —
        // reuse it rather than re-deriving it here.
        assignedName: data.assigned_to ? adminDisplayName(assignee, t) : null,
        inboundToAddress,
        inboundOriginalText,
      };
    },
    enabled: !!ticketId,
  });

  const { data: previousCaseCount = 0 } = useQuery({
    queryKey: [
      "support-ticket-detail",
      ticketId,
      "previous-cases",
      ticket?.reported_by,
    ],
    enabled: supportAppearance && !!ticketId && !!ticket?.reported_by,
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from("issues")
        .select("id", { count: "exact", head: true })
        .eq("reported_by", ticket!.reported_by)
        .neq("id", ticketId!);
      if (error) throw error;
      return count ?? 0;
    },
  });

  const handleStatusChange = async (value: string) => {
    await onStatusChange?.(value);
    void refetch();
  };
  const handleAssign = async (value: string) => {
    const userId = value === UNASSIGNED ? null : value;
    await onAssign?.(userId);
    void refetch();
  };

  // Tenant picker for "Link to tenant…" — a small profile search by name or
  // email for an unowned conversation.
  const normalizedLinkSearch = linkSearch.trim();
  const { data: linkProfiles } = useQuery({
    queryKey: ["support-link-profiles", normalizedLinkSearch],
    enabled: linkOpen,
    queryFn: async (): Promise<ProfileBrief[]> => {
      let q = supabase
        .from("profiles")
        .select("id, name, email")
        .order("name", { ascending: true })
        .limit(20);
      const safeTerm = safeOrTerm(normalizedLinkSearch);
      if (safeTerm) {
        q = q.or(`name.ilike.%${safeTerm}%,email.ilike.%${safeTerm}%`);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data || []) as ProfileBrief[];
    },
  });

  const linkToTenant = useMutation({
    // Resolves to whether the guard matched: false = someone linked this
    // ticket first, and nothing was written.
    mutationFn: async (tenant: ProfileBrief): Promise<boolean> => {
      // An UPDATE of the existing ticket, never an insert. `.is("user_id",
      // null)` is first-writer-wins: it must never re-home a ticket that
      // already belongs to someone. `.select("id").maybeSingle()` hands back
      // the row only if that guard matched — null means zero rows, so the UI
      // can say what actually happened instead of assuming success.
      const { data, error } = await supabase
        .from("issues")
        .update({
          user_id: tenant.id,
          reported_by: tenant.id,
          contact_type: "tenant",
        })
        .eq("id", ticketId)
        .is("user_id", null)
        .select("id")
        .maybeSingle();
      if (error) throw error;
      if (data) {
        const email = inboundSenderEmail(ticket?.metadata);
        const phone = inboundSenderPhone(ticket?.metadata);
        const identity = email
          ? { p_type: "email", p_value: email }
          : phone
            ? { p_type: "phone", p_value: phone }
            : null;
        if (identity) {
          const { error: rememberError } = await supabase.rpc(
            "remember_support_contact" as never,
            {
              ...identity,
              p_profile_id: tenant.id,
              p_contact_type: "tenant",
            } as never
          );
          if (rememberError)
            console.warn(
              "Could not remember support contact identity",
              rememberError
            );
        }
      }
      return data != null;
    },
    onSuccess: (linked) => {
      setLinkOpen(false);
      setLinkSearch("");
      // Linking changes who can SEE the ticket (tenant RLS keys on user_id),
      // so the inbox list + counts and this pane's own row all refetch —
      // also when nothing was written, so the pane shows the real owner.
      queryClient.invalidateQueries({ queryKey: ["support-inbox"] });
      queryClient.invalidateQueries({
        queryKey: ["support-ticket-detail", ticketId],
      });
      if (linked) {
        toast.success(
          t("support.detail.linkedToTenant", "Ticket linked to tenant")
        );
      } else {
        toast.warning(
          t(
            "support.detail.linkAlreadyOwned",
            "This ticket already belongs to a tenant — nothing was changed."
          )
        );
      }
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error
          ? error.message
          : t(
              "support.detail.linkFailed",
              "Couldn't link this ticket to a tenant."
            )
      );
    },
  });

  const markUnknownAsExternal = async () => {
    if (!ticket) return;
    const { error: updateError } = await supabase
      .from("issues")
      .update({ contact_type: "external" })
      .eq("id", ticket.id);
    if (updateError) {
      toast.error(
        t(
          "support.unknown.markFailed",
          "Couldn't mark this sender as an external contact."
        )
      );
      return;
    }
    const email = inboundSenderEmail(ticket.metadata);
    const phone = inboundSenderPhone(ticket.metadata);
    const identity = email
      ? { p_type: "email", p_value: email }
      : phone
        ? { p_type: "phone", p_value: phone }
        : null;
    if (identity) {
      const { error } = await supabase.rpc(
        "remember_support_contact" as never,
        {
          ...identity,
          p_profile_id: null,
          p_contact_type: "external",
        } as never
      );
      if (error)
        toast.error(
          t(
            "support.unknown.rememberFailed",
            "Contact type changed, but future messages could not be linked automatically."
          )
        );
    }
    void refetch();
  };

  // A failed refetch AFTER a successful load (access revoked mid-session, an
  // outage, refetchOnWindowFocus, or handleStatusChange/handleAssign's own
  // `refetch()` above) must not blank the pane: react-query keeps `ticket`
  // at its last good value on a failed refetch, so `ticket !== undefined`
  // here means there IS still something to show. Blanking it anyway would
  // unmount both IssueChatThread instances and, with them, the composer's
  // locally-held draft text — turning an invisible background failure into
  // one that destroys whatever an admin was mid-typing. Non-blocking toast
  // instead, same pattern IssueChatThread.tsx's postComment.onError uses.
  //
  // This condition must stay the exact complement of the blocking branch's
  // `ticketError && ticket === undefined` below — if the two ever decouple,
  // a failure could hit neither branch (or both), and the covering test can
  // keep passing without proving anything about the branch that regressed.
  useEffect(() => {
    if (ticketError && ticket !== undefined) {
      const msg =
        ticketError instanceof Error
          ? ticketError.message
          : t("support.detail.loadError", "Couldn't load this ticket.");
      // Stable id: `t` is a fresh function on every LanguageProvider
      // re-render (a plain arrow inside a new context-value object each
      // time), so this effect re-runs — and would re-toast the SAME
      // unresolved error — on any unrelated provider render, not just a new
      // failure. refetchOnWindowFocus is on for the translations query
      // itself (LanguageContext.tsx), so a window refocus during an
      // ongoing outage is a real trigger, not a hypothetical one. An id
      // makes a repeat replace the existing toast instead of stacking.
      toast.error(msg, { id: "ticket-detail-refetch-error" });
    }
  }, [ticketError, ticket, t]);

  if (!ticketId) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center text-sm text-muted-foreground">
        {t("support.detail.empty", "Select a ticket to see its conversation.")}
      </div>
    );
  }

  // Only the INITIAL load gets the blocking error + retry state — `ticket`
  // is still `undefined` (never successfully loaded) here, so there is
  // nothing on screen yet to preserve. Once loaded, a later failure is
  // handled by the effect above instead (see its comment).
  if (ticketError && ticket === undefined) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center text-sm text-muted-foreground">
        <p>{t("support.detail.loadError", "Couldn't load this ticket.")}</p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          {t("support.detail.retry", "Retry")}
        </Button>
      </div>
    );
  }

  const channelMeta = ticket
    ? SUPPORT_CHANNELS.find((c) => c.key === ticket.channel)
    : null;
  const propertyRoom = ticket
    ? [ticket.properties?.title, ticket.rooms?.title]
        .filter(Boolean)
        .join(" · ")
    : "";
  const contactTypeLabel = ticket
    ? ticket.contact_type === "owner"
      ? t("support.contact.owner", "Owner")
      : ticket.contact_type === "external"
        ? t("support.contact.external", "External")
        : t("support.contact.tenant", "Tenant")
    : "";
  const rosterHasAssignee =
    !!ticket?.assigned_to && roster.some((a) => a.id === ticket.assigned_to);

  return (
    <div
      className={
        supportAppearance
          ? "support-ticket-detail flex h-full flex-col"
          : "flex h-full flex-col"
      }
    >
      <div className="support-detail-header border-b border-border/60 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            {/* Item 6: once a ticket is linked to a tenant, the name opens
                the full tenant profile. An unowned ticket (reported_by NULL)
                has no profile to open, so the name stays plain text there
                rather than becoming a link that goes nowhere. */}
            {ticket?.reported_by ? (
              <button
                type="button"
                onClick={() => setProfileOpen(true)}
                className="max-w-full truncate rounded-sm font-semibold text-foreground underline decoration-dotted underline-offset-4 hover:decoration-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {ticket.tenantName}
              </button>
            ) : (
              <p className="truncate font-semibold text-foreground">
                {ticket === undefined
                  ? t("support.detail.loading", "Loading…")
                  : ticket === null
                    ? t(
                        "support.detail.notFound",
                        "This ticket could not be found."
                      )
                    : ticket.tenantName}
              </p>
            )}
            {supportAppearance && (
              <div className="support-contact-context">
                {[
                  contactTypeLabel,
                  ticket?.properties?.address || ticket?.properties?.title,
                  ticket?.rooms?.title,
                  ticket?.rooms?.size_sqm
                    ? `${ticket.rooms.size_sqm} m²`
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
                {ticket?.reported_by && (
                  <>
                    {" "}
                    ·{" "}
                    <button
                      className="font-semibold underline text-[#092E2C]"
                      onClick={() => setProfileOpen(true)}
                    >
                      {t(
                        previousCaseCount === 1
                          ? "support.detail.previousCase"
                          : "support.detail.previousCases",
                        previousCaseCount === 1
                          ? "{count} previous case"
                          : "{count} previous cases"
                      ).replace("{count}", String(previousCaseCount))}
                    </button>
                  </>
                )}
              </div>
            )}
            {!supportAppearance && (
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                {propertyRoom ? <span>{propertyRoom}</span> : null}
                {channelMeta ? (
                  <Badge variant="outline">
                    {t(channelMeta.i18nKey, channelMeta.fallback)}
                  </Badge>
                ) : null}
                {ticket?.tenantEmail ? <span>{ticket.tenantEmail}</span> : null}
                {ticket?.inboundToAddress ? (
                  <span data-testid="inbound-to-address">
                    {t("support.detail.receivedAt", "to")}{" "}
                    {ticket.inboundToAddress}
                  </span>
                ) : null}
              </div>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {ticket && !ticket.reported_by ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex h-8 items-center gap-1 text-xs font-semibold text-[#092E2C] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {t("support.unknown.banner", "Unknown sender")}
                    <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[190px]">
                  <DropdownMenuItem
                    onSelect={() => {
                      setLinkSearch("");
                      setLinkOpen(true);
                    }}
                  >
                    {t(
                      "support.detail.linkDialogTitle",
                      "Link this ticket to a tenant"
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => void markUnknownAsExternal()}
                  >
                    {t(
                      "support.unknown.markExternal",
                      "Mark as external contact"
                    )}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
            {!supportAppearance && (
              <Select
                value={ticket?.status ?? ""}
                onValueChange={handleStatusChange}
                disabled={!ticket}
              >
                <SelectTrigger
                  className="h-8 w-[150px]"
                  aria-label={t("support.detail.status", "Status")}
                >
                  <SelectValue
                    placeholder={t("support.detail.loading", "Loading…")}
                  />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {t(o.i18nKey, o.fallback)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Select
              value={ticket ? (ticket.assigned_to ?? UNASSIGNED) : ""}
              onValueChange={handleAssign}
              disabled={!ticket}
            >
              <SelectTrigger
                className="h-8 w-[150px] text-xs font-semibold"
                aria-label={t("support.detail.assignee", "Assignee")}
              >
                <SelectValue
                  placeholder={t("support.detail.loading", "Loading…")}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED}>
                  {t("support.detail.unassigned", "Unassigned")}
                </SelectItem>
                {rosterError ? (
                  <SelectItem value="__roster_error" disabled>
                    {t("support.detail.rosterError", "Couldn't load admins")}
                  </SelectItem>
                ) : null}
                {roster.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {adminDisplayName(a, t)}
                  </SelectItem>
                ))}
                {/* Keep a valid option if the current assignee lost their
                    admin role (or the roster hasn't loaded yet) — same guard
                    the old Kanban console's assignee Select used. */}
                {ticket?.assigned_to && !rosterHasAssignee ? (
                  // ticket.assignedName is already adminDisplayName(...) —
                  // never null once assigned_to is set, see the queryFn above.
                  <SelectItem value={ticket.assigned_to}>
                    {ticket.assignedName}
                  </SelectItem>
                ) : null}
              </SelectContent>
            </Select>
            {supportAppearance && ticket && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-full border-[#092E2C]"
                onClick={() =>
                  void handleStatusChange(
                    ticket.archived_at ||
                      ["resolved", "closed", "cancelled"].includes(
                        ticket.status
                      )
                      ? "open"
                      : "resolved"
                  )
                }
              >
                <Check size={14} />
                {ticket.archived_at ||
                ["resolved", "closed", "cancelled"].includes(ticket.status)
                  ? t("support.detail.reopen", "Reopen")
                  : t("support.detail.complete", "Done")}
              </Button>
            )}
          </div>
        </div>
      </div>
      {supportAppearance && ticket && (
        <SupportPresentationCards metadata={ticket.metadata} section="bundle" />
      )}
      <Tabs
        defaultValue="conversation"
        className="flex min-h-0 flex-1 flex-col"
      >
        {/* Sized and coloured from the design's own tab group rather than the
            shadcn default: a warm #F0EEE9 track (the cool `bg-muted` sat badly
            against this card), 2px of track padding, 7px inner radius and an
            11px semibold label. The row carries its own padding and a divider
            so the tabs stop butting straight into the message band — there was
            no breathing room between them at all. */}
        <div
          className={
            supportAppearance
              ? "hidden"
              : "shrink-0 border-b border-border/60 px-4 py-2.5"
          }
        >
          <TabsList className="h-auto w-fit gap-0 rounded-[9px] bg-[#F0EEE9] p-0.5">
            <TabsTrigger
              value="conversation"
              className="rounded-[7px] px-3 py-[5px] text-[11px] font-semibold text-[#5C6E6C] data-[state=active]:bg-white data-[state=active]:text-[#0F4C4A]"
            >
              {t("support.detail.conversationTab", "Conversation")}
            </TabsTrigger>
            <TabsTrigger
              value="internal"
              className="gap-1.5 rounded-[7px] px-3 py-[5px] text-[11px] font-semibold text-[#5C6E6C] data-[state=active]:bg-white data-[state=active]:text-[#0F4C4A]"
            >
              {t("support.detail.internalNotesTab", "Internal notes")}
              {/* Per the design, and one of the gaps on Neel's checklist:
                  without it there is no way to tell a ticket with five staff
                  notes from one with none without opening the tab. */}
              {internalCount > 0 ? (
                <span
                  className="flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-[#8A6B54] px-1 text-[9px] font-bold leading-none text-white"
                  aria-label={t(
                    "support.detail.internalNotesCount",
                    "{n} internal notes"
                  ).replace("{n}", String(internalCount))}
                >
                  {internalCount > 99 ? "99+" : internalCount}
                </span>
              ) : null}
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="conversation" className="mt-0 min-h-0 flex-1">
          <IssueChatThread
            // I7 fix — without a key, switching tickets reuses this exact
            // component instance: neither `fromAddress` nor `sendState`
            // (nor any other local composer state) resets on `issueId`
            // changing, so picking "contact@…" on ticket A and then opening
            // ticket B would send B's next reply from A's chosen address. A
            // key tied to the ticket forces a full remount on every switch.
            key={ticketId}
            issueId={ticketId}
            currentUserId={currentUserId}
            queryKeyPrefix="ticket-detail"
            hideInternal={!supportAppearance}
            canPostInternal={supportAppearance}
            supportAppearance={supportAppearance}
            hideHeader
            showViewBadge={false}
            readScope="admin"
            senderScope="admin"
            channel={ticket?.channel}
            // The Support Inbox conversation is allowed to dispatch outbound
            // replies and move the ticket to waiting.
            dispatchOutbound
            onOutboundSent={onConversationSent}
            focusCommentId={focusCommentId}
            translatedFrom={
              ticket
                ? readSupportPresentation(ticket.metadata).translation?.from
                : undefined
            }
            originalDescription={
              ticket
                ? (readSupportPresentation(ticket.metadata).translation
                    ?.originalText ??
                  ticket.inboundOriginalText ??
                  undefined)
                : undefined
            }
            internalNotePlaceholder={t(
              "support.notes.placeholder",
              "Note for the team – {name} won't see this"
            ).replace(
              "{name}",
              ticket?.tenantName ?? t("support.contact.tenant", "the tenant")
            )}
          />
        </TabsContent>

        {/* The flex column lives on an inner wrapper, NOT on TabsContent.
            Radix marks the inactive panel `hidden`, but that only hides it via
            the UA rule `[hidden] { display: none }` — which a `flex` class on
            the same element overrides. The panel then stayed laid out at
            `flex-1` and took half the card's height from the Conversation tab,
            leaving a dead gap under the composer. Keeping TabsContent
            display:block (like the Conversation panel) lets `hidden` win. */}
        <TabsContent value="internal" className="mt-0 min-h-0 flex-1">
          <div className="flex h-full min-h-0 flex-col">
            <p className="shrink-0 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
              {t(
                "support.detail.internalNotesWarning",
                "Internal notes are never visible to the tenant."
              )}
            </p>
            <div className="min-h-0 flex-1">
              <IssueChatThread
                issueId={ticketId}
                currentUserId={currentUserId}
                queryKeyPrefix="ticket-detail"
                internalOnly
                hideHeader
                showViewBadge={false}
                readScope="admin"
                senderScope="admin"
              />
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* "Link to tenant…" picker over all profiles; a stranger's ticket has
          no existing tenant to start from. */}
      <Dialog
        open={linkOpen}
        onOpenChange={(open) => {
          if (!open) {
            setLinkOpen(false);
            setLinkSearch("");
          }
        }}
      >
        <DialogContent className="sm:max-w-[440px] rounded-2xl">
          <DialogHeader>
            <DialogTitle>
              {t(
                "support.detail.linkDialogTitle",
                "Link this ticket to a tenant"
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={linkSearch}
                onChange={(e) => setLinkSearch(e.target.value)}
                placeholder={t(
                  "support.triage.searchTenants",
                  "Search tenants by name or email"
                )}
                className="h-10 rounded-xl pl-10"
                autoFocus
              />
            </div>

            <div className="max-h-[280px] space-y-1.5 overflow-y-auto pr-1">
              {(linkProfiles || []).map((p) => (
                <button
                  key={p.id}
                  type="button"
                  disabled={linkToTenant.isPending}
                  className="flex w-full items-center gap-2.5 rounded-xl border border-border/60 bg-background px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted disabled:opacity-50"
                  onClick={() => linkToTenant.mutate(p)}
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium">
                      {p.name || p.email || t("adminIssues.unknown", "Unknown")}
                    </span>
                    {p.email ? (
                      <span className="block truncate text-xs text-muted-foreground">
                        {p.email}
                      </span>
                    ) : null}
                  </span>
                </button>
              ))}
              {(linkProfiles || []).length === 0 ? (
                <div className="rounded-xl border border-dashed border-border/60 bg-muted/20 p-4 text-center text-sm text-muted-foreground">
                  {t(
                    "support.triage.noTenantsMatch",
                    "No tenants match this search."
                  )}
                </div>
              ) : null}
            </div>

            <div className="text-[11px] text-muted-foreground">
              {t(
                "support.detail.linkHint",
                "The ticket and its conversation stay as they are — it becomes this tenant's and appears in their portal."
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <TenantProfileLauncher
        userId={ticket?.reported_by ?? null}
        tenantName={ticket?.tenantName ?? null}
        tenantEmail={ticket?.tenantEmail ?? null}
        ticketId={ticketId}
        compact={supportAppearance}
        open={profileOpen}
        onOpenChange={setProfileOpen}
      />
    </div>
  );
}
